/**
 * Relay transit (plan R17, §2.9 flow 7; spec §4.1). In normal flight `relayHint` tracks the nearest relay within
 * `hintRangeM` (its gates from R6's `transitGate`, `online` from the state). Crossing its disc outward (against
 * `inward`) while it is online, aligned and slow enough detaches the ship and starts the transit:
 * - `enter` (`enterS`): the ship coasts on through the ring;
 * - `tunnel`: `session.enterSystem(dest, { kind: 'relay', relayId })` (both systems hold a site for the relay), and the
 *   tunnel lasts at least `tunnelMinS`, until the destination is ready (past `tunnelMaxS` it is logged as a budget
 *   miss, and the tunnel holds until the system is ready);
 * - `exit` (`exitS`): the ship is attached at the session's arrival pose (`arrivalOffsetM` inward, `arrivalSpeed`),
 *   `REV.transitEnd` fires; then normal flight.
 * A rejected switch re-enters the origin system at the same relay's arrival pose (`REV.transitEnd` b = 1), logged.
 *
 * `checkRelay` and `stepTransit` run per tick and allocate nothing (D-014); starting a transit and the system switch
 * are rare events.
 */
import { tollBeforeTransit } from '../../charter/tolls'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import type { RelaySite } from '../../galaxy/layout/types'
import { emitReach, REV } from '../../reach/events'
import type { ReachCore } from '../../reach/runtime/types'
import { attachShip, detachShip, isDetached, type Sim } from '../../sim'
import { Pos64, PrevPos64, Vel } from '../../world/components'
import type { TravelConfig } from '../config'
import { discCrossing, transitGate, tunnelPhase, type TunnelPhase } from '../math'
import { speedOf } from './flight'
import { TRANSIT_END, type TravelRuntime } from './state'
import { cancelCharge } from './transitions'

/**
 * M10 P5a11a (Q5): a refusal installed by the parked-L feature (`features/parkedL`): given the origin system id it
 * returns the refusal text (`fleet-link/parkedL.ts parkedJumpRefusal`) or null. Asked only on the crossing tick, so
 * nothing runs per tick; the hook itself allocates nothing (a cached string). Null (the default): no refusal.
 */
let jumpRefusal: ((systemId: string) => string | null) | null = null

/** Installs (or with null removes) the relay jump refusal; returns the uninstall. */
export function installJumpRefusal(fn: ((systemId: string) => string | null) | null): () => void {
  jumpRefusal = fn
  return () => {
    if (jumpRefusal === fn) jumpRefusal = null
  }
}

/** Whether relay `r` is online: the state's status when it has one, else the generator's flag. */
export function relayOnline(core: ReachCore, r: RelaySite): boolean {
  const rs = core.state.relays[r.relayId]
  if (rs !== undefined) return rs.status === 'online'
  return core.galaxy.relays[r.index]?.online === true
}

/**
 * The transit phase at `t` s since it started, R6's `tunnelPhase` with one change: until the destination is ready
 * the tunnel holds (never `exit` into a system that is not there), and a destination ready after `tunnelMaxS` gets
 * its full `exitS` from the moment it became ready (`readyAt`).
 */
export function transitPhaseAt(t: number, ready: boolean, readyAt: number, cfg: TravelConfig): TunnelPhase {
  const c = cfg.transit
  if (t < c.enterS) return 'enter'
  if (!ready) return 'tunnel'
  if (readyAt > c.enterS + c.tunnelMaxS) {
    if (t < readyAt) return 'tunnel'
    return t < readyAt + c.exitS ? 'exit' : 'done'
  }
  return tunnelPhase(t, ready, cfg, readyAt)
}

/** In normal flight: updates the relay hint and starts a transit through the nearest relay when it is crossed. */
export function checkRelay(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): boolean {
  const layout = rt.layout
  const h = core.travel.relayHint
  const cfg = rt.configs.travel
  if (layout === null) {
    h.relayIndex = -1
    return false
  }
  const cx = Pos64.x[eid]
  const cy = Pos64.y[eid]
  const cz = Pos64.z[eid]
  const relays = layout.relays
  let best = -1
  let bestD2 = Infinity
  for (let i = 0; i < relays.length; i++) {
    const p = relays[i].position
    const dx = p[0] - cx
    const dy = p[1] - cy
    const dz = p[2] - cz
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 < bestD2) {
      bestD2 = d2
      best = i
    }
  }
  const range = cfg.transit.hintRangeM
  if (best < 0 || bestD2 > range * range) {
    h.relayIndex = -1
    h.distance = best < 0 ? 0 : Math.sqrt(bestD2)
    h.aligned = false
    h.slowEnough = false
    h.online = false
    return false
  }
  const r = relays[best]
  const n = rt.nose
  transitGate(n.x, n.y, n.z, speedOf(eid), -r.inward[0], -r.inward[1], -r.inward[2], cfg, rt.gate)
  h.relayIndex = r.index
  h.distance = Math.sqrt(bestD2)
  h.aligned = rt.gate.aligned
  h.slowEnough = rt.gate.slowEnough
  h.online = relayOnline(core, r)
  const p = r.position
  const crossed = discCrossing(PrevPos64.x[eid], PrevPos64.y[eid], PrevPos64.z[eid], cx, cy, cz, p[0], p[1], p[2], r.inward[0], r.inward[1], r.inward[2], r.apertureM, rt.disc)
  if (!crossed || rt.disc.sign >= 0 || !h.online || !h.aligned || !h.slowEnough) return false
  if (jumpRefusal !== null) {
    const why = jumpRefusal(layout.systemId)
    if (why !== null) {
      // The crossing is one tick, so this logs once per refused pass (rare), never per tick.
      logger.info(`reach: relay jump ${r.relayId} refused: ${why}`)
      return false
    }
  }
  startTransit(rt, core, sim, eid, r)
  return true
}

/** Starts a transit through relay `r` (rare): the ship detaches and coasts on into the enter phase. */
function startTransit(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number, r: RelaySite): void {
  const t = core.travel
  const layout = rt.layout
  if (layout === null) return
  if (t.mode === 'charging') cancelCharge(core, eid)
  const dest = core.galaxy.systems.find((s) => s.id === r.otherSystemId)
  detachShip(sim, eid)
  const run = rt.transit
  run.relayId = r.relayId
  run.originId = layout.systemId
  run.serial++
  run.readyAt = 0
  run.failed = false
  run.fallbackStarted = false
  run.overrun = false
  t.mode = 'transit'
  t.chargeS = 0
  t.speed = speedOf(eid)
  const tr = t.transit
  tr.phase = 'enter'
  tr.t = 0
  tr.ready = false
  tr.destSystemIndex = dest === undefined ? -1 : dest.index
  t.relayHint.relayIndex = -1
  logger.info(`reach: relay jump ${r.relayId} ${layout.systemId} → ${r.otherSystemId}`)
  // M5 (S-M3-10, plan C15): the Charter toll of the destination's holder, paid or evaded; the jump always goes.
  tollBeforeTransit(core.career, r.relayId, r.otherSystemId)
  emitReach(core.events, REV.transitStart, r.index, tr.destSystemIndex, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** Marks the switch of transit `serial` ready (ignored when that transit is over). */
function markReady(rt: TravelRuntime, core: ReachCore, serial: number): void {
  const t = core.travel
  if (serial !== rt.transit.serial || t.mode !== 'transit') return
  t.transit.ready = true
  rt.transit.readyAt = t.transit.t
}

/** Starts the system switch to the destination (rare). */
function beginSwitch(rt: TravelRuntime, core: ReachCore): void {
  const run = rt.transit
  const serial = run.serial
  const dest = core.galaxy.systems[core.travel.transit.destSystemIndex]
  if (dest === undefined) {
    logger.error(`reach: relay ${run.relayId} leads to no known system; staying in ${run.originId}`)
    run.failed = true
    return
  }
  core.session.enterSystem(dest.id, { kind: 'relay', relayId: run.relayId }).then(
    () => markReady(rt, core, serial),
    (err: unknown) => {
      if (serial !== run.serial) return
      logger.error(`reach: relay jump to ${dest.id} failed; returning to ${run.originId}`, err)
      run.failed = true
    },
  )
}

/** After a failed switch (rare): re-enter the origin system at the relay's arrival pose. */
function beginFallback(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const run = rt.transit
  const serial = run.serial
  run.fallbackStarted = true
  const origin = core.galaxy.systems.find((s) => s.id === run.originId)
  core.travel.transit.destSystemIndex = origin === undefined ? -1 : origin.index
  core.session.enterSystem(run.originId, { kind: 'relay', relayId: run.relayId }).then(
    () => markReady(rt, core, serial),
    (err: unknown) => {
      if (serial !== run.serial) return
      logger.error(`reach: could not return to ${run.originId} after a failed relay jump`, err)
      if (eid >= 0 && isDetached(sim, eid)) attachShip(sim, eid)
      finishTransit(core)
    },
  )
}

/** Back to normal flight after a transit. */
function finishTransit(core: ReachCore): void {
  const t = core.travel
  const tr = t.transit
  t.mode = 'normal'
  tr.phase = 'none'
  tr.t = 0
  tr.ready = false
  tr.destSystemIndex = -1
}

/** One tick of a relay transit. Allocation-free apart from the rare switch start. */
export function stepTransit(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  const tr = t.transit
  const run = rt.transit
  const cfg = rt.configs.travel
  tr.t += SIM_DT
  if (tr.phase === 'enter') {
    if (tr.t < cfg.transit.enterS) {
      // The ship coasts on through the ring (float64, like any detached ship).
      if (eid >= 0) {
        Pos64.x[eid] += Vel.x[eid] * SIM_DT
        Pos64.y[eid] += Vel.y[eid] * SIM_DT
        Pos64.z[eid] += Vel.z[eid] * SIM_DT
      }
      return
    }
    tr.phase = 'tunnel'
    beginSwitch(rt, core)
    return
  }
  if (tr.phase === 'tunnel') {
    if (!tr.ready) {
      if (run.failed && !run.fallbackStarted) beginFallback(rt, core, sim, eid)
      if (!run.overrun && tr.t >= cfg.transit.enterS + cfg.transit.tunnelMaxS) {
        run.overrun = true
        logger.warn(`reach: budget miss: the relay tunnel ran past ${cfg.transit.tunnelMaxS} s waiting for the destination`)
      }
      return
    }
    const phase = transitPhaseAt(tr.t, true, run.readyAt, cfg)
    if (phase === 'enter' || phase === 'tunnel') return
    tr.phase = 'exit'
    if (eid >= 0 && isDetached(sim, eid)) attachShip(sim, eid)
    t.speed = eid >= 0 ? speedOf(eid) : 0
    const x = eid >= 0 ? Pos64.x[eid] : 0
    const y = eid >= 0 ? Pos64.y[eid] : 0
    const z = eid >= 0 ? Pos64.z[eid] : 0
    emitReach(core.events, REV.transitEnd, tr.destSystemIndex, run.failed ? TRANSIT_END.failed : TRANSIT_END.arrived, x, y, z, 0)
  }
  if (tr.phase === 'exit' && transitPhaseAt(tr.t, true, run.readyAt, cfg) === 'done') finishTransit(core)
}
